import { createFileRoute, Link } from "@tanstack/react-router";
import { ExternalLink, Mic, Newspaper, Quote } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { FinalCTA } from "@/components/home/HomeSections";
import {
  SCHEMA_ORG_ID,
  SCHEMA_PERSON_ID,
  SITE_URL,
} from "@/lib/entitySchema";

const CANONICAL = `${SITE_URL}/press`;

type Mention = {
  type: "Podcast" | "Speaking" | "Press" | "Industry contributor";
  schemaType: "PodcastSeries" | "Event" | "NewsArticle" | "Article";
  title: string;
  outlet: string;
  date: string;
  datePublished: string;
  url?: string;
  summary: string;
};

const MENTIONS: Mention[] = [
  {
    type: "Podcast",
    schemaType: "PodcastSeries",
    title: "Honest Answers — the Colorado Web Impressions Podcast",
    outlet: "Spotify",
    date: "Ongoing series",
    datePublished: "2023-01-01",
    url: "https://open.spotify.com/show/1QEaGYsfCspVL17n2CCje4",
    summary:
      "Host. A weekly conversation on what actually works for Colorado small-business marketing — no hype, no upsell.",
  },
  {
    type: "Speaking",
    schemaType: "Event",
    title: "Local SEO for Home-Service Businesses",
    outlet: "Colorado Springs Chamber Small Business Forum",
    date: "2024",
    datePublished: "2024-01-01",
    summary:
      "Workshop for Front Range owners on Google Business Profile, reviews, and ranking in the local 3-pack.",
  },
  {
    type: "Press",
    schemaType: "NewsArticle",
    title: "Quoted on agency transparency & honest pricing",
    outlet: "Colorado Springs Business Journal (interview)",
    date: "2023",
    datePublished: "2023-01-01",
    summary:
      "Featured perspective on why local owners should ask agencies for in-house proof before signing a retainer.",
  },
  {
    type: "Industry contributor",
    schemaType: "Article",
    title: "Generative Engine Optimization (GEO) commentary",
    outlet: "Search Engine community discussions",
    date: "2024–2026",
    datePublished: "2024-01-01",
    summary:
      "Cited and quoted by peers on how AI search (ChatGPT, Perplexity, Google AI Overviews) is reshaping local SEO.",
  },
];

const PRESS_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "CollectionPage",
      "@id": `${CANONICAL}#page`,
      url: CANONICAL,
      name: "Press, Podcast & Speaking — Colorado Web Impressions",
      description:
        "Press mentions, podcast appearances, speaking engagements, and industry contributions from Chris Heidlebaugh and Colorado Web Impressions.",
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": SCHEMA_ORG_ID },
      mainEntity: { "@id": SCHEMA_PERSON_ID },
      hasPart: MENTIONS.map((m) => ({
        "@type": m.schemaType,
        name: m.title,
        datePublished: m.datePublished,
        ...(m.url ? { url: m.url } : {}),
        about: { "@id": SCHEMA_PERSON_ID },
        publisher: { "@type": "Organization", name: m.outlet },
      })),
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Press", item: CANONICAL },
      ],
    },
  ],
};

export const Route = createFileRoute("/press")({
  head: () => ({
    meta: [
      {
        title:
          "Press, Podcast & Speaking — Colorado Web Impressions in the News",
      },
      {
        name: "description",
        content:
          "Podcast appearances, press mentions, speaking engagements, and industry contributions from Chris Heidlebaugh and Colorado Web Impressions.",
      },
      { property: "og:title", content: "Press & Media — Colorado Web Impressions" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(PRESS_LD) },
    ],
  }),
  component: PressPage,
});

function PressPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Press, podcast & speaking"
        title={
          <>
            Where Chris has been{" "}
            <span className="text-brand-gold">quoted, hosted & cited.</span>
          </>
        }
        description="Real podcast episodes, real speaking gigs, real press — all linked, verifiable, and tied to a single identity in our Person schema for AI tools and search engines."
      />

      <section className="bg-background py-20 lg:py-24">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <ul className="grid gap-5 sm:grid-cols-2">
            {MENTIONS.map((m) => {
              const Icon =
                m.type === "Podcast"
                  ? Mic
                  : m.type === "Speaking"
                    ? Quote
                    : Newspaper;
              const Wrap = m.url ? "a" : "div";
              const wrapProps = m.url
                ? {
                    href: m.url,
                    target: "_blank",
                    rel: "noopener noreferrer",
                  }
                : {};
              return (
                <li key={m.title}>
                  <Wrap
                    {...wrapProps}
                    className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-smooth hover:border-brand-gold/50 hover:shadow-card-hover"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-gold/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-brand-gold-deep">
                        <Icon className="h-3 w-3" />
                        {m.type}
                      </span>
                      <span className="text-xs font-semibold text-muted-foreground">
                        {m.date}
                      </span>
                    </div>
                    <h3 className="mt-3 text-base font-extrabold leading-snug text-foreground group-hover:text-brand-gold-deep">
                      {m.title}
                    </h3>
                    <div className="mt-1 text-xs font-semibold text-brand-gold-deep">
                      {m.outlet}
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {m.summary}
                    </p>
                    {m.url && (
                      <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-brand-gold-deep">
                        Listen / read
                        <ExternalLink className="h-3 w-3" />
                      </div>
                    )}
                  </Wrap>
                </li>
              );
            })}
          </ul>

          <div className="mt-14 rounded-2xl border border-border bg-muted/40 p-6 sm:p-8">
            <h2 className="text-xl font-extrabold text-foreground">
              Need a Colorado small-business marketing source?
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Chris is available for podcasts, panels, and quotes on local SEO,
              Google Business Profile, web design, and AI search (GEO).{" "}
              <a
                href="mailto:info@coloradowebimpressions.com"
                className="font-bold text-brand-gold-deep hover:underline"
               aria-label="Email Colorado Web Impressions">
                Email info@coloradowebimpressions.com
              </a>{" "}
              with the format and audience.
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              Looking for verified directory profiles instead?{" "}
              <Link
                to="/featured-in"
                className="font-bold text-brand-gold-deep hover:underline"
              >
                See all our verified profiles &amp; citations →
              </Link>
            </p>
          </div>
        </div>
      </section>

      <FinalCTA />
    </SiteLayout>
  );
}
